import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import { useDialogControllers, IconInfoInverted, ButtonIcon } from '@machobear/design-system'
import { select } from '@/store'

import UtilitiesTypeModal from './utilities-type-modal'
import FeatureList from './feature-list'
import CustomList from './custom-list'
import { FormBox, FormBoxSkeleton } from '../../components'
import { DetailsFormValues, Feature } from '../../types'

const APPLIANCES = ['dishwasher', 'fridge', 'microwave', 'oven', 'stove_top', 'tv_unit']
const LAUNDRY = ['coin_laundry', 'dryer', 'washer']
const GARBAGE = ['garbage_chute', 'garburator', 'sewage_disposal']
const OTHER = [
  'air_conditioning',
  'bathtub',
  'jacuzzi',
  'carpets',
  'bbq',
  'snow_removal',
  'disable_ready',
  'windows_covering'
]

const FeaturesSection = ({ isLoading }: { isLoading: boolean }) => {
  const { t } = useTranslation()
  const { values } = useFormikContext<DetailsFormValues>()

  const { visible, showModal, closeModal } = useDialogControllers({
    utilitiesType: false
  })

  const features: Feature[] = useSelector(select.basic.features)
  const utilities: Feature[] = useSelector(select.basic.utilities)

  const filterFeatures = useCallback(
    (group: string[]) => features.filter(feature => group.includes(feature.txt_id)),
    [features]
  )

  const featureGroups = useMemo(
    () => ({
      appliances: filterFeatures(APPLIANCES),
      laundry: filterFeatures(LAUNDRY),
      garbage: filterFeatures(GARBAGE),
      other: filterFeatures(OTHER)
    }),
    [filterFeatures]
  )

  return (
    <>
      <UtilitiesTypeModal
        visible={visible.utilitiesType}
        closeModal={closeModal('utilitiesType')}
      />
      <FormBox label={t('Features & Utilities', 'Features & Utilities')}>
        {isLoading ? (
          <FormBoxSkeleton />
        ) : (
          <>
            <FeatureList
              label={t('Appliances', 'Appliances')}
              name="unit_features"
              options={featureGroups.appliances}
              formValues={values}
            />
            <FeatureList
              label={t('Laundry', 'Laundry')}
              name="unit_features"
              options={featureGroups.laundry}
              formValues={values}
            />
            <FeatureList
              label={t('Garbage disposal', 'Garbage disposal')}
              name="unit_features"
              options={featureGroups.garbage}
              formValues={values}
            />
            <FeatureList
              label={t('Other', 'Other')}
              name="unit_features"
              options={featureGroups.other}
              formValues={values}
            />
            <CustomList
              label={t('Custom features', 'Custom features')}
              name="custom_features"
              options={values.custom_features}
              formValues={values}
              placeholder={t('Enter custom feature', 'Enter custom feature')}
            />
            <FeatureList
              label={t('Utilities', 'Utilities')}
              tooltip={
                <ButtonIcon
                  id="property-type-info"
                  label={t('Utility type info')}
                  variant="transparent"
                  icon={<IconInfoInverted />}
                  onClick={showModal('utilitiesType')}
                  tone="secondary"
                />
              }
              name="listing_utilities"
              options={utilities}
              typeOptions={['included', 'shared', 'metered']}
              formValues={values}
              defaultType="included"
              translationKeyBase="utilities"
            />
            <CustomList
              label={t('Custom utilities', 'Custom utilities')}
              name="custom_utilities"
              options={values.custom_utilities}
              typeOptions={['included', 'shared', 'metered']}
              formValues={values}
              defaultType="included"
              placeholder={t('Enter custom utility', 'Enter custom utility')}
            />
          </>
        )}
      </FormBox>
    </>
  )
}

export default FeaturesSection
